import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import type { AuthAdapter, AuthConfigLike } from "./types.js";

/**
 * Authorisation code with PKCE against the identity provider named by
 * /auth/config (D-23). Tokens live in session storage for the tab; renewal
 * is silent while the provider's session is alive, which the provider caps
 * at twelve hours.
 */
export function createOidcAuth(
  config: AuthConfigLike,
  origin = window.location.origin,
): AuthAdapter {
  const manager = new UserManager({
    authority: config.issuer,
    client_id: config.clientId,
    redirect_uri: `${origin}/callback`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    scope: "openid profile email groups",
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());
  manager.events.addUserLoaded(notify);
  manager.events.addUserUnloaded(notify);
  manager.events.addAccessTokenExpired(notify);
  manager.events.addSilentRenewError(notify);
  return {
    mode: "oidc",
    async getToken() {
      const user = await manager.getUser();
      return user && !user.expired ? user.access_token : null;
    },
    async signIn() {
      await manager.signinRedirect();
    },
    async completeSignIn() {
      await manager.signinCallback();
    },
    async signOut() {
      await manager.signoutRedirect();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
